/**
 * Version Check Hook
 * Handles API version handshake to ensure frontend is compatible with backend
 *
 * Features:
 * - Compares local APP_VERSION against backend's frontendMinVersion
 * - Returns whether the frontend is outdated
 * - Provides a function to manually trigger version check
 */

import { useCallback, useState } from 'react'
import { env } from '@/config/env'
import { getVersion, isVersionOutdated } from '@/api/version'

export interface UseVersionCheckReturn {
    /**
     * Whether the current frontend version is older than the minimum required
     */
    isOutdated: boolean

    /**
     * Whether a version check is currently in progress
     */
    isLoading: boolean

    /**
     * Error message if the version check failed (null if no error)
     */
    error: string | null

    /**
     * Manually trigger a version check
     * @returns true if the frontend is outdated, false otherwise
     */
    checkVersion: () => Promise<boolean>
}

/**
 * Hook for checking if the frontend version is compatible with the backend
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isOutdated, checkVersion } = useVersionCheck()
 *
 *   useEffect(() => {
 *     // Check version when user returns to the app
 *     const handleVisibilityChange = () => {
 *       if (document.visibilityState === 'visible') {
 *         checkVersion().then(outdated => {
 *           if (outdated) {
 *             window.location.reload()
 *           }
 *         })
 *       }
 *     }
 *
 *     document.addEventListener('visibilitychange', handleVisibilityChange)
 *     return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
 *   }, [checkVersion])
 *
 *   return null
 * }
 * ```
 */
export function useVersionCheck(): UseVersionCheckReturn {
    const [isOutdated, setIsOutdated] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const checkVersion = useCallback(async (): Promise<boolean> => {
        setIsLoading(true)
        setError(null)

        try {
            const versionInfo = await getVersion()

            if (!versionInfo) {
                // Version check failed, but don't block the app
                // User can continue with potentially stale version
                console.warn(
                    '[Version Check] Could not fetch version info, skipping check'
                )
                setIsLoading(false)
                return false
            }

            const currentVersion = env.APP_VERSION
            const minVersion = versionInfo.frontendMinVersion

            console.log(
                `[Version Check] Current: ${currentVersion}, Min required: ${minVersion}`
            )

            const outdated = isVersionOutdated(currentVersion, minVersion)

            if (outdated) {
                console.log(
                    '[Version Check] Frontend is outdated, reload required'
                )
            }

            setIsOutdated(outdated)
            setIsLoading(false)

            return outdated
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : 'Unknown error'
            console.error('[Version Check] Error:', errorMessage)
            setError(errorMessage)
            setIsLoading(false)
            return false
        }
    }, [])

    return {
        isOutdated,
        isLoading,
        error,
        checkVersion,
    }
}

export default useVersionCheck

